import { TrashSimple } from '@phosphor-icons/react'
import { Button, Popconfirm, Table, type TableColumnsType } from 'antd'
import moment from 'moment'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

import useSearchParams from '@/hooks/useSearchParams'
import { DeleteMeasureProps } from '@/queries/measures/services/deleteMeasure'
import { useDeleteMeasure } from '@/queries/measures/useDeleteMeasure'
import { useLoadMeasuresByFormAndClientId } from '@/queries/measures/useLoadMeasuresByFormAndClientId'
import Tooltip from '@/ui/atoms/Tooltip'
import cn from '@/utils/tailwind/cn'

import { MeasureBadge } from '../Components/MeasureBadge'
import { NotesByAssessment } from '../Components/NotesByAssessment'
import { getCurrentQuestionById } from '../utils/getCurrentQuestionById'
import { transformLevelsToCategories } from '../utils/transformLevelsToCategories'

type DeleteButtonProps = {
  onClick: () => void
  isManual: boolean
}

const DeleteButton = ({ onClick, isManual }: DeleteButtonProps) => {
  let tooltipTitle = 'Delete Measure'

  if (!isManual) tooltipTitle = 'You can only delete manually created measures'
  return (
    <Tooltip mouseEnterDelay={0.35} title={tooltipTitle}>
      <Popconfirm
        classNames={{ body: '[&_.ant-btn]:!py-3.5' }}
        description="Are you sure you want to delete this Measure?"
        disabled={!isManual}
        onConfirm={onClick}
        title="Delete"
      >
        <Button
          className={cn(
            'w-[fit-content] !px-2.5 hover:!bg-gray-100 focus:outline-none',
            {
              '!cursor-not-allowed': !isManual,
            }
          )}
          type="link"
        >
          <TrashSimple
            className={cn(
              'text-xl text-gray-500',
              !isManual && 'text-gray-300'
            )}
          />
        </Button>
      </Popconfirm>
    </Tooltip>
  )
}

export const TableView = () => {
  const { clientId = '' } = useParams<{ clientId?: string }>()
  const searchParams = useSearchParams()

  const formId = searchParams.get('formId') || ''
  const questionId = searchParams.get('question')

  const { deleteMeasure } = useDeleteMeasure({ clientId, formId })

  const { data: measureData } = useLoadMeasuresByFormAndClientId(
    formId,
    clientId || ''
  )

  const onConfirmDelete = async (data: DeleteMeasureProps) => {
    try {
      await deleteMeasure(data)
      toast.success('Measure deleted successfully')
    } catch (error) {
      toast.error('Error while deleted a measure')
    }
  }

  const currentQuestion = getCurrentQuestionById({
    data: measureData,
    questionId,
  })

  const columns: TableColumnsType = [
    {
      title: 'Date',
      dataIndex: 'date',
      width: 130,
    },
    {
      title: 'Notes',
      dataIndex: 'notes',
    },
    {
      title: 'Score',
      dataIndex: 'score',
      align: 'center',
      width: 150,
    },
    {
      title: 'Level',
      dataIndex: 'level',
      align: 'center',
      width: 150,
    },
    {
      title: 'Actions',
      dataIndex: 'actions',
      align: 'center',
      width: 100,
    },
  ]

  const levels = transformLevelsToCategories(currentQuestion?.levels)
  const hasLevels = Boolean(currentQuestion?.levels?.length)

  const dataSource = currentQuestion?.measures.map((item) => {
    const level = levels?.(Number(item.answer_value))
    return {
      key: item.id,
      date: moment(item.answer_date, 'YYYY-MM-DD').format('MM/DD/YYYY'),
      notes: <NotesByAssessment {...{ ...item }} />,
      score: Number(item.answer_value),
      level: (
        <div className="flex w-full justify-center">
          {hasLevels ? (
            <MeasureBadge
              className="mt-1 w-[fit-content]"
              style={{
                background: `${level?.color}30`,
                color: level?.color,
              }}
            >
              {level?.label}
            </MeasureBadge>
          ) : null}
          {!hasLevels ? (
            <MeasureBadge className="mt-1 w-[fit-content]" color="none">
              None
            </MeasureBadge>
          ) : null}
        </div>
      ),
      actions: (
        <div className="flex w-full justify-center">
          <DeleteButton
            isManual={Boolean(item.manually_created)}
            onClick={() =>
              onConfirmDelete({
                clientId: clientId || '',
                formId,
                questionId: questionId || '',
                measureId: item.id,
              })
            }
          />
        </div>
      ),
    }
  })

  return (
    <div className="w-full flex-col gap-4 rounded border border-gray-50 bg-white">
      <Table
        className="[&_thead_tr]:bg-white [&_thead_tr_th]:!bg-white"
        columns={columns}
        dataSource={dataSource}
        pagination={false}
        scroll={{ y: 55 * 8 }}
      />
    </div>
  )
}
